<template>
  <PieChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    is-polarity
    class="make-a-copy"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import PieChart from '@/components/cards/models/PieChart';
import sentimentModel from '@/mixins/sentiment-model';
import { mapActions } from 'vuex';
import EventBus from '@/utils/event-bus';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import { POST_FIELDS, OPERATION, DATE_PORTION } from '@/services/charts-service';

export default {
  name: 'SentimentPieChartPNP',
  components: {
    PieChart,
  },
  mixins: [
    sentimentModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      axesSeriesName: [],
    };
  },
  computed: {
    series() {
      if (this.rawSeries && this.axesSeriesName) {
        return this.axesSeriesName.map(serieName => {
          const serie = this.rawSeries.find(serie => serie.name === serieName);
          if (!serie) {
            return {
              color: this.getSentimentColor(serieName),
              name: serieName,
              y: 0,
            };
          }
          return {
            color: this.getSentimentColor(serie.name),
            name: serie.name,
            y: serie.data[0] || 0,
          };
        });
      }
      return [];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      const promisses = [
        this.createRequisitions(DATA_TYPE.LISTENING),
        this.createRequisitions(DATA_TYPE.SAC),
      ];
      Promise.all(
        promisses
      ).then(([responseListening, responseSac]) => {
        const response = this.aggregateResponses(responseListening, responseSac);
        const normalizedResponse = this.normalizeSentimentResponseFromChartService(response);
        this.axesSeriesName = normalizedResponse?.axes[1]?.values;
        this.rawSeries = normalizedResponse.points;

        if (!this.axesSeriesName) {
          this.axesSeriesName = ['Negativo', 'Neutro', 'Positivo'];
        }

        this.rawSeries = this.newSentimentArray(this.rawSeries);

        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }
      })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    createRequisitions(dataType) {
      return this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        filterType: dataType,
        params: {
          datePortion: DATE_PORTION.BY_YEAR,
          grouping: [POST_FIELDS.POLARITY],
          operators: this.operation,
        },
      });
    },
    aggregateResponses(res1, res2) {
      const pointsMap = new Map();

      const addPoints = (points) => {
        points.forEach(({ name, data }) => {
          if (!pointsMap.has(name)) {
            pointsMap.set(name, Array(data.length).fill(0));
          }
          pointsMap.set(name, pointsMap.get(name).map((val, idx) => val + data[idx]));
        });
      };

      addPoints(res1.points);
      addPoints(res2.points);
      const aggregatedPoints = Array.from(pointsMap, ([name, data]) => ({ name, data }));

      const axes = JSON.parse(JSON.stringify(res1.axes || []));

      if (!axes[1]) {
        axes[1] = {};
      }

      axes[1].values = aggregatedPoints.map(p => p.name);

      return {
        points: aggregatedPoints,
        axes,
      };
    },
    newSentimentArray(arr) {
      const validateArray = arr.length === 1 && arr[0].data.length === 1 && arr[0].name === undefined;

      this.requestStatus = 'success';

      if (validateArray) {
        const newArray = [
          { 'name': 'Neutro', 'data': [0] },
          { 'name': 'Positivo', 'data': [0] },
          { 'name': 'Negativo', 'data': [0] },
        ];

        return newArray;
      }
      return arr;
    },
  },
};
</script>
